import { useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";

const MandatoryRedirector = () => {
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  const history = useHistory();
  const location = useLocation();

  // Track current mandatory (id + type)
  const currentMandatoryRef = useRef({ id: null, type: null });

  const isMounted = useRef(true);

  // FIX #1: Keep location in a ref so polling/SSE always see the latest path
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // FIX #2: Track retry attempts for SSE reconnect
  // NOTE: SSE reconnect is now handled entirely inside SseService with
  // exponential backoff. These refs are kept only for logging purposes.
  const sseRetryCount = useRef(0);
  const sseRetryTimer = useRef(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (sseRetryTimer.current) clearTimeout(sseRetryTimer.current);
    };
  }, []);

  // ─────────────────────────────────────────────
  // REDIRECT HANDLER
  // ─────────────────────────────────────────────
  const redirectToMandatory = useCallback(
    (id, type, source = "") => {
      if (!isMounted.current) return;

      const pathMap = {
        SURVEY: `/take/${id}`,
        OFFER: `/offers`,
        TRAINING: `/training-learning`,
      };

      const targetPath = pathMap[type];
      if (!targetPath) return;

      // FIX #1: Read from ref — always has the current pathname
      const currentPath = locationRef.current.pathname;

      const isSameMandatory =
        currentMandatoryRef.current.id === id &&
        currentMandatoryRef.current.type === type;

      console.log(`🔒 Redirect [${source}] →`, type, id);

      if (!isSameMandatory || currentPath !== targetPath) {
        toast.dismiss();
        if (type?.toUpperCase() === "SURVEY") {
          toast.success(`Complete mandatory quiz or survey first!`, {
            duration: 1000,
          });
        } else {
          toast.success(`Complete mandatory ${type.toLowerCase()} first!`, {
            duration: 1000,
          });
        }
      }

      currentMandatoryRef.current = { id, type };

      if (currentPath !== targetPath) {
        history.replace(targetPath);
      }
    },
    [history],
  );

  // ─────────────────────────────────────────────
  // CENTRAL CHECK (PRIORITY BASED)
  // ─────────────────────────────────────────────
  const checkMandatory = useCallback(
    async (source = "") => {
      try {
        const [surveyRes, offerRes, trainingRes] = await Promise.all([
          api.fetchMandatorySurvey(userId),
          api.fetchOffersForUser(userId),
          api.fetchUserTrainings(userId),
        ]);

        // FIX #3: Guard after await — component may have unmounted during API calls
        if (!isMounted.current) return;

        const survey = surveyRes.data?.data?.content?.find(
          (i) => i.isMandatory && !i.isParticipated && i.status === true,
        );

        const offer = offerRes.data?.data?.find(
          (i) => i.isMandatory && !i.isViewed && i.status === "active",
        );

        const training = trainingRes.data?.data?.find(
          (i) =>
            i.isMandatory &&
            !(i.status === "completed" || (i.progress ?? 0) > 0),
        );

        // PRIORITY: SURVEY > OFFER > TRAINING
        if (survey) {
          return redirectToMandatory(survey.id, "SURVEY", source);
        }

        if (offer) {
          return redirectToMandatory(offer.id, "OFFER", source);
        }

        if (training) {
          return redirectToMandatory(training.id, "TRAINING", source);
        }

        // No mandatory → reset
        currentMandatoryRef.current = { id: null, type: null };
      } catch (err) {
        console.error(err);
      }
    },
    [userId, redirectToMandatory],
  );

  // ─────────────────────────────────────────────
  // RUN ON ROUTE CHANGE
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    const timeout = setTimeout(() => {
      checkMandatory("REST");
    }, 200);

    return () => clearTimeout(timeout);
  }, [location.pathname, userId, checkMandatory]);

  // ─────────────────────────────────────────────
  // SSE LISTENER
  // FIX: onOpen and onError now wire directly into SseService via api.connectSse.
  //      isSseConnected state is read from api.isSseConnected() — the single
  //      source of truth — instead of a separate ref that was never being set.
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!userId || !username || !token) return;

    api.connectSse({
      userId,
      token,

      // ✅ FIX: onOpen is now actually called by SseService._openStream()
      //    when the HTTP 200 response is received. This is the real signal
      //    that the stream is live. sseService._isConnected is already true
      //    at this point, so api.isSseConnected() will return true immediately.
      onOpen: () => {
        console.log("🟢 SSE connected");
        sseRetryCount.current = 0;

        // Run an immediate check when SSE (re)connects so we don't miss
        // anything that arrived during a reconnect window.
        checkMandatory("SSE-RECONNECT");
      },

      // ✅ FIX: onError fires from SseService whenever the stream drops.
      //    sseService._isConnected is already false at this point, so
      //    api.isSseConnected() will return false and polling will take over.
      onError: () => {
        console.log("🔴 SSE error — polling will take over");
        // SseService handles its own exponential-backoff reconnect internally.
        // Nothing extra needed here.
      },

      onAuthError: () => {
        console.warn("🔒 SSE auth error — not retrying");
        // Token is invalid; don't retry. Polling continues as fallback.
      },

      onMandatoryPush: (data) => {
        console.log(
          "🔍 RAW SSE onMandatoryPush fired:",
          JSON.stringify(data, null, 2),
        );
        if (!data?.isMandatory || !data?.id || !data?.type) {
          console.warn("⚠️ SSE data missing fields:", {
            isMandatory: data?.isMandatory,
            id: data?.id,
            type: data?.type,
          });
          return;
        }
        redirectToMandatory(data.id, data.type, "SSE");
      },
    });

    return () => {
      api.disconnectSse();
      if (sseRetryTimer.current) {
        clearTimeout(sseRetryTimer.current);
        sseRetryTimer.current = null;
      }
    };
  }, [userId, username, token, checkMandatory, redirectToMandatory]);

  // ─────────────────────────────────────────────
  // POLLING FALLBACK
  // Fires every 20s but skips if SSE is live.
  // ✅ FIX: reads api.isSseConnected() — the real source of truth from
  //    SseService — instead of a local ref that was permanently false.
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    const interval = setInterval(() => {
      if (api.isSseConnected()) {
        console.log("⏭️ SSE active — skipping poll");
        return;
      }

      console.log("⏱️ Polling fallback...");
      checkMandatory("POLLING");
    }, 20000);

    return () => clearInterval(interval);
  }, [userId, checkMandatory]);

  return null;
};

export default MandatoryRedirector;
